import { useCallback, useEffect, useRef, useState } from "react";
import { DA_ProductPrice } from "@danishagro/shared/src/interfaces/price.interface";
import { getPricePath } from "@danishagro/shared/src/helpers/getPricePath.helper";
import { useAppData } from "../contexts/appData.context";
import { useRequest } from "./useRequest.hook";

const createFingerprint = (customerNumber, items) => {
    const prefix = `${customerNumber}--`;
    const itemsString = items
        .map(({ productNumber, retailVariantId, variantEntityId }) =>
            [
                productNumber,
                retailVariantId ?? "",
                variantEntityId ? "selected" : "not-selected",
            ].join("::")
        )
        .join("|");
    return `${prefix}${itemsString}`;
};

type ProductsPriceItem = {
    productNumber: string;
    retailVariantId?: string | null;
    variantEntityId?: string | null;
};
type ReturnType<T> = T extends ProductsPriceItem[] ? DA_ProductPrice[] : DA_ProductPrice;

export const useBatchedProductPrices = <P extends ProductsPriceItem[][]>(
    chunks: P,
    context?: string // Optional context (categoryId/URL) for validation
): DA_ProductPrice[] => {
    const [priceData, setPriceData] = useState<(DA_ProductPrice | undefined)[]>([]);
    const [_processedChunks, setProcessedChunks] = useState<number>(0);

    const { customerNumber, showPrices } = useAppData();
    const request = useRequest();

    // Ref to store abort controllers for each batch
    const abortControllersRef = useRef<AbortController[]>([]);
    // Ref to store current context for validation
    const currentContextRef = useRef<string | undefined>(context);

    const getProductDetailsPrices = useCallback(
        (customerNumber, products, variantSelected?: boolean, options?) =>
            request
                .post(
                    getPricePath(customerNumber, variantSelected),
                    products.filter(({ productNumber }) => productNumber),
                    options
                )
                .then((response) => response.json())
                .then(({ productPrices }) => productPrices),
        [request]
    );

    useEffect(() => {
        if (!showPrices || !chunks || chunks.length === 0) {
            return;
        }

        // Update current context
        currentContextRef.current = context;

        // Cancel all existing requests when context changes
        abortControllersRef.current.forEach((controller) => {
            if (!controller.signal.aborted) {
                controller.abort();
            }
        });
        abortControllersRef.current = [];

        // Reset state when chunks change
        setPriceData([]);
        setProcessedChunks(0);

        // Calculate total products for initial array size
        const totalProducts = chunks.flat().length;
        setPriceData(new Array(totalProducts).fill(undefined));

        // Process each chunk sequentially
        const processChunk = async (chunkIndex: number) => {
            const chunk = chunks[chunkIndex];
            if (!chunk || chunk.length === 0) return;

            // Create abort controller for this chunk
            const abortController = new AbortController();
            abortControllersRef.current[chunkIndex] = abortController;

            const productsForRequest = chunk.map(({ productNumber, retailVariantId }) => ({
                productNumber,
                variantId: retailVariantId ?? undefined,
            }));

            try {
                const response = await getProductDetailsPrices(
                    customerNumber,
                    productsForRequest,
                    null,
                    {
                        signal: abortController.signal,
                    }
                );

                const chunkPriceData = productsForRequest.map(
                    ({ productNumber: productNumberTarget }) => {
                        const {
                            productNumber,
                            priceWithVat,
                            priceWithoutVat,
                            currency,
                            quantityDiscounts,
                            variantDiscounts,
                            discount,
                            enviromentCharges,
                            hasDiscount,
                        } = response.find(
                            ({ productNumber }) => productNumber === productNumberTarget
                        ) || {
                            productNumber: productNumberTarget,
                            priceWithVat: 0,
                            priceWithoutVat: 0,
                            currency: "",
                            quantityDiscounts: [],
                            variantDiscounts: [],
                            discount: {},
                            price: {},
                            listPrice: 0,
                            hasDiscount: false,
                        };

                        return {
                            productNumber,
                            priceWithVat,
                            priceWithoutVat,
                            currency,
                            quantityDiscounts,
                            variantDiscounts,
                            discount,
                            environmentCharges: enviromentCharges,
                            hasDiscount,
                        };
                    }
                );

                // Validate context before updating state (only if context is provided)
                if (context && currentContextRef.current !== context) {
                    console.log(
                        `Ignoring price response for chunk ${chunkIndex} - context changed`
                    );
                    return;
                }

                // Update price data for this chunk
                setPriceData((current) => {
                    const newData = [...current];
                    const startIndex = chunks.slice(0, chunkIndex).flat().length;
                    chunkPriceData.forEach((price, index) => {
                        newData[startIndex + index] = price;
                    });
                    return newData;
                });

                setProcessedChunks(chunkIndex + 1);

                // Process next chunk with a small delay
                if (chunkIndex + 1 < chunks.length) {
                    setTimeout(() => processChunk(chunkIndex + 1), 100);
                }
            } catch (error) {
                // Ignore abort errors - they're intentional cancellations
                if (error?.name === "AbortError") {
                    console.log(`Price request for chunk ${chunkIndex} was aborted`);
                    return;
                }

                console.error(`Error fetching prices for chunk ${chunkIndex}:`, error);

                // Validate context before updating state (only if context is provided)
                if (context && currentContextRef.current !== context) {
                    console.log(`Ignoring price error for chunk ${chunkIndex} - context changed`);
                    return;
                }

                // Set undefined for failed chunk
                setPriceData((current) => {
                    const newData = [...current];
                    const startIndex = chunks.slice(0, chunkIndex).flat().length;
                    chunk.forEach((_, index) => {
                        newData[startIndex + index] = undefined;
                    });
                    return newData;
                });

                setProcessedChunks(chunkIndex + 1);

                // Continue with next chunk even if this one failed
                if (chunkIndex + 1 < chunks.length) {
                    setTimeout(() => processChunk(chunkIndex + 1), 100);
                }
            }
        };

        // Start processing from the first chunk
        processChunk(0);

        // Cleanup function to abort all requests when component unmounts or dependencies change
        return () => {
            abortControllersRef.current.forEach((controller) => {
                if (!controller.signal.aborted) {
                    controller.abort();
                }
            });
            abortControllersRef.current = [];
        };
    }, [customerNumber, chunks, showPrices, getProductDetailsPrices, context]);

    return priceData;
};

export const useProductPrices = <P extends ProductsPriceItem | ProductsPriceItem[]>(
    items: P,
    getDetails = false
): ReturnType<P> => {
    const [priceData, setPriceData] = useState<(DA_ProductPrice | undefined)[]>([]);

    const { customerNumber, showPrices } = useAppData();
    const request = useRequest();

    const pricesCount = useRef(0);
    const fingerprint = useRef("");
    const controllerRef = useRef<AbortController | null>();

    const getProductListPrices = useCallback(
        (customerNumber, products, options?) =>
            request
                .post(
                    getPricePath(customerNumber),
                    products.filter(({ productNumber }) => productNumber),
                    options
                )
                .then((response) => response.json())
                .then(({ productPrices }) => productPrices),
        [request]
    );

    const getProductDetailsPrices = useCallback(
        (customerNumber, products, variantSelected?: boolean, options?) =>
            request
                .post(
                    getPricePath(customerNumber, variantSelected),
                    products.filter(({ productNumber }) => productNumber),
                    options
                )
                .then((response) => response.json())
                .then(({ productPrices }) => productPrices),
        [request]
    );

    useEffect(() => {
        if (!showPrices) {
            return undefined;
        }

        // Make sure items are in an array
        const productItems = Array.isArray(items) ? items : [items];
        // Create fingerprint and match with previous fingerprint to find out
        // if it's necessary to reset the prices (if the list of products has changed)
        const newFingerprint = createFingerprint(customerNumber, productItems);
        const isResetNecessary = !newFingerprint.startsWith(fingerprint.current);
        // Find out if this is a request for more prices
        // (probably due to loading more products on scroll)
        const isRequestForMore =
            !isResetNecessary && newFingerprint.length > fingerprint.current.length;

        if (isResetNecessary) {
            // Reset prices immediately to avoid showing old prices
            setPriceData([]);
        }

        if (
            (isResetNecessary || isRequestForMore) &&
            productItems.length &&
            newFingerprint !== fingerprint.current
        ) {
            if (controllerRef.current) {
                // If a request has already been sent to the API,
                // abort it before sending a new one
                controllerRef.current.abort();
            }

            // Save fingerprint and create new AbortController
            fingerprint.current = newFingerprint;
            controllerRef.current = new AbortController();

            const itemsWithoutPriceData = productItems.slice(
                isResetNecessary ? 0 : pricesCount.current
            );
            const variantSelected = itemsWithoutPriceData.some(({ variantEntityId }) =>
                Boolean(variantEntityId)
            );

            // Isolate the products that we haven't already received prices for
            const productsWithoutPriceData = itemsWithoutPriceData.map(
                ({ productNumber, retailVariantId }) => ({
                    productNumber,
                    variantId: retailVariantId ?? undefined,
                })
            );

            // Request the API
            getProductDetailsPrices(
                customerNumber,
                productsWithoutPriceData,
                productItems.length > 1 || !getDetails ? null : variantSelected,
                {
                    signal: controllerRef.current.signal,
                }
            )
                .then((response) => {
                    // Map the received prices with the product numbers from the request.
                    // If a price is missing, insert an empty price object.

                    const newPriceData = productsWithoutPriceData.map(
                        ({ productNumber: productNumberTarget }) => {
                            const {
                                productNumber,
                                priceWithVat,
                                priceWithoutVat,
                                currency,
                                quantityDiscounts,
                                variantDiscounts,
                                discount,
                                enviromentCharges,
                                hasDiscount,
                            } = response.find(
                                ({ productNumber }) => productNumber === productNumberTarget
                            ) || {
                                productNumber: productNumberTarget,
                                priceWithVat: 0,
                                priceWithoutVat: 0,
                                currency: "",
                                quantityDiscounts: [],
                                variantDiscounts: [],
                                discount: {},
                                price: {},
                                listPrice: 0,
                                hasDiscount: false,
                            };

                            return {
                                productNumber,
                                priceWithVat,
                                priceWithoutVat,
                                currency,
                                quantityDiscounts,
                                variantDiscounts,
                                discount,
                                environmentCharges: enviromentCharges,
                                hasDiscount,
                            };
                        }
                    );

                    // Save the new prices and update price count
                    setPriceData((current) => {
                        const newValue = [...(isResetNecessary ? [] : current), ...newPriceData];
                        pricesCount.current = newValue.length;
                        return newValue;
                    });
                    controllerRef.current = null;
                })
                .catch((err) => {
                    // Ignore abort errors, since they only occur
                    // when we initiate them ourselves (a few lines up)
                    if (err?.name !== "AbortError") {
                        console.log(err);
                        setPriceData((current) => {
                            const newValue = [
                                ...(isResetNecessary ? [] : current),
                                ...productsWithoutPriceData.map(() => undefined),
                            ];
                            pricesCount.current = newValue.length;
                            return newValue;
                        });
                        controllerRef.current = null;
                    }
                });
        }
    }, [
        items,
        getDetails,
        getProductListPrices,
        getProductDetailsPrices,
        customerNumber,
        showPrices,
    ]);

    return (Array.isArray(items) ? priceData : priceData[0]) as ReturnType<P>;
};
